<template>
  <div>
    <div
      v-if="feedback"
      class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5"
    >
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Feedback', to: ROUTES.ADMIN.FEEDBACK.FEEDBACK_LIST },
            {
              title: feedback.uuid,
              to: ROUTES.ADMIN.FEEDBACK.FEEDBACK_DETAIL.replace(':uuid', feedback.uuid)
            }
          ]"
        />
      </div>
      <!-- End Page Header -->

      <!-- Feedback Detail Card -->
      <div class="flex flex-col space-y-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <!-- Metadata Section -->
        <div>
          <h2 class="mb-4 text-xl font-semibold text-gray-800">Feedback Details</h2>

          <!-- User Info at the top of metadata -->
          <div
            v-if="userObject"
            class="mb-4 flex items-center gap-3"
          >
            <UserAvatar
              :user="userObject"
              size="40px"
            />
            <div>
              <div class="font-medium text-gray-900">
                {{ userObject.first_name }} {{ userObject.last_name }}
              </div>
              <div class="text-sm text-gray-500">User</div>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <!-- Feature -->
            <div>
              <label class="mb-2 block text-sm font-medium text-gray-700">Feature</label>
              <div
                class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800"
              >
                {{ getFeatureDisplayName(feedback.feature) }}
              </div>
            </div>

            <!-- Rating -->
            <div>
              <label class="mb-2 block text-sm font-medium text-gray-700">Rating</label>
              <div
                class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800"
              >
                {{ feedback.rating }}
              </div>
            </div>

            <!-- Created At -->
            <div>
              <label class="mb-2 block text-sm font-medium text-gray-700">Created At</label>
              <div
                class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800"
              >
                {{ formatDate(feedback.created_at) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Comment Section -->
        <div>
          <h2 class="mb-3 text-xl font-semibold text-gray-800">Comment</h2>
          <div class="min-h-[100px] rounded-md border border-gray-200 bg-gray-50 p-4 text-gray-800">
            {{ feedback.comment || 'No comment provided' }}
          </div>
        </div>

        <!-- Related Data Section -->
        <div v-if="hasRelatedData">
          <h2 class="mb-3 text-xl font-semibold text-gray-800">Related Data</h2>

          <!-- Document Generator Related Data -->
          <div
            v-if="feedback.related_data.document_generator"
            class="mt-3"
          >
            <h3 class="text-lg font-medium text-gray-700">Document Generator</h3>
            <div class="mt-2 rounded-md border border-gray-200 bg-gray-50 p-4">
              <DocumentGeneratorFeedback
                :template-uuid="feedback.related_data.document_generator.template_uuid"
                :document-uuid="feedback.related_data.document_generator.document_uuid"
              />
            </div>
          </div>

          <!-- AI Chat Related Data -->
          <div
            v-if="feedback.related_data.ai_chat"
            class="mt-3"
          >
            <h3 class="text-lg font-medium text-gray-700">AI Chat</h3>
            <div class="mt-2 rounded-md border border-gray-200 bg-gray-50 p-4">
              <div
                v-if="isLoadingChat"
                class="flex justify-center p-4"
              >
                <div
                  class="h-6 w-6 animate-spin rounded-full border-b-2 border-t-2 border-purple-600"
                ></div>
              </div>

              <div
                v-else-if="!chatSession"
                class="p-4 text-center text-gray-500"
              >
                No chat session data available
              </div>

              <div v-else>
                <div class="mb-3 flex items-center justify-between">
                  <h4 class="text-md font-medium text-gray-700">
                    Chat Session: {{ chatSession.title || chatSession.uuid }}
                  </h4>
                  <span class="text-xs text-gray-500">
                    {{ formatDate(chatSession.created_at) }}
                  </span>
                </div>

                <div class="mt-6 border-t border-gray-200 pt-5">
                  <h3 class="mb-3 text-lg font-medium text-gray-700">Chat Messages</h3>
                  <div class="space-y-3">
                    <AdminChatMessage
                      v-for="(msg, index) in chatSession.messages"
                      :key="index"
                      :message="msg"
                      :is-highlighted="isHighlightedMessage(msg)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- AI Analysis Related Data -->
          <div
            v-if="feedback.related_data.ai_analysis"
            class="mt-3"
          >
            <h3 class="text-lg font-medium text-gray-700">AI Analysis</h3>
            <div class="mt-2 rounded-md border border-gray-200 bg-gray-50 p-4">
              <AiAnalysisFeedback
                :ai-augmentation-uuid="feedback.related_data.ai_analysis.ai_augmentation_uuid"
              />
            </div>
          </div>

          <!-- Agent Action Related Data -->
          <div
            v-if="feedback.related_data.agent_action"
            class="mt-3"
          >
            <h3 class="text-lg font-medium text-gray-700">Agent Action</h3>
            <div class="mt-2 rounded-md border border-gray-200 bg-gray-50 p-4">
              <AgentActionFeedback
                :agent-action-uuid="feedback.related_data.agent_action.agent_action_uuid"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-else
      class="flex h-64 items-center justify-center"
    >
      <div class="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import { useRoute } from 'vue-router'
  import { FeedbackService } from '@/types/generated/services.gen'
  import {
    type Feedback,
    type SuperUserFeedback,
    type SuperUserUser,
    FeatureEnum
  } from '@/types/generated/types.gen'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import UserAvatar from '@/components/app/users/UserAvatar.vue'
  import type { CustomerOrganisationUser as User } from '@/types/generated'
  import AdminChatMessage from '@/components/admin/chats/AdminChatMessage.vue'
  import DocumentGeneratorFeedback from '@/components/admin/DocumentGeneratorFeedback.vue'
  import AiAnalysisFeedback from '@/components/admin/AiAnalysisFeedback.vue'
  import AgentActionFeedback from '@/components/admin/AgentActionFeedback.vue'
  import {
    AiChatSessionsService,
    type AIChatSession,
    type AIChatSessionMessage
  } from '@/types/generated'

  const route = useRoute()
  const params = route.params as { uuid: string }

  const feedback = ref<Feedback | SuperUserFeedback | null>(null)
  const userObject = ref<User | null>(null)
  const chatSession = ref<AIChatSession | null>(null)
  const isLoadingChat = ref(false)
  const reviewedMessageUuid = ref<string | null>(null)

  onMounted(async () => {
    try {
      const fetchedFeedback = await FeedbackService.feedbackRetrieve({ uuid: params.uuid })
      feedback.value = fetchedFeedback

      // Check if feedback has a user object (SuperUserFeedback) or just a user ID (Feedback)
      if (
        feedback.value &&
        typeof feedback.value.user === 'object' &&
        feedback.value.user !== null
      ) {
        // For SuperUserFeedback, the user is already an object
        userObject.value = feedback.value.user as unknown as User
      }

      if (fetchedFeedback.related_data?.ai_chat) {
        reviewedMessageUuid.value =
          fetchedFeedback.related_data.ai_chat.ai_chat_session_message_uuid

        // Fetch chat session data
        if (fetchedFeedback.related_data.ai_chat.ai_chat_session_uuid) {
          await fetchChatSessionData(fetchedFeedback.related_data.ai_chat.ai_chat_session_uuid)
        }
      }
    } catch (error) {
      console.error('Error fetching feedback:', error)
    }
  })

  const hasRelatedData = computed(() => {
    if (!feedback.value?.related_data) return false

    return !!(
      feedback.value.related_data.document_generator ||
      feedback.value.related_data.ai_chat ||
      feedback.value.related_data.ai_analysis ||
      feedback.value.related_data.agent_action
    )
  })

  const getFeatureDisplayName = (feature: string) => {
    switch (feature) {
      case FeatureEnum.AI_CHAT:
        return 'AI Chat'
      case FeatureEnum.DOCUMENT_GENERATOR:
        return 'Document Generator'
      case FeatureEnum.AI_ANALYSIS:
        return 'AI Analysis'
      case FeatureEnum.AGENT_ACTION:
        return 'Agent Action'
      case FeatureEnum.CASE_AI_WORKFLOW:
        return 'Case AI Workflow'
      case FeatureEnum.NEW_CASE_PAGE:
        return 'New Case Page'
      default:
        return feature
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const isHighlightedMessage = (message: AIChatSessionMessage) => {
    return message.uuid === reviewedMessageUuid.value
  }

  const fetchChatSessionData = async (aiChatSessionUuid: string) => {
    isLoadingChat.value = true
    try {
      const response = await AiChatSessionsService.aiChatSessionsRetrieve({
        uuid: aiChatSessionUuid
      })
      chatSession.value = response
    } catch (error) {
      console.error('Error fetching chat session:', error)
    } finally {
      isLoadingChat.value = false
    }
  }
</script>

<style scoped></style>
